import { useState, useCallback } from "react";
import { N8N_WEBHOOKS } from "../constants";
import { securelyTriggerN8n } from "../lib/n8n";

// =============================================================================
// USE CONVERSATION RESYNC HOOK
// =============================================================================
// PURPOSE: Manually trigger a full resync of a single conversation from GHL.
// This fetches ALL messages for the conversation and upserts them to Supabase.
//
// USE CASE: When the automated 40-second sync misses older messages in a
// conversation that hasn't been active recently.
//
// FLOW: Frontend → N8N Webhook → GHL API → Supabase
// =============================================================================

export interface ResyncResult {
  success: boolean;
  messages_synced?: number;
  total_from_ghl?: number;
  filtered_system_messages?: number;
  synced_at?: string;
  error?: string;
  note?: string;
}

export interface UseConversationResyncResult {
  /** Trigger a resync for the given conversation */
  resync: (conversationId: string) => Promise<ResyncResult>;
  /** Whether a resync is currently in progress */
  isResyncing: boolean;
  /** The result of the last resync attempt */
  result: ResyncResult | null;
  /** Error message if the resync failed */
  error: string | null;
  /** Clear the result/error state */
  reset: () => void;
}

export function useConversationResync(): UseConversationResyncResult {
  const [isResyncing, setIsResyncing] = useState(false);
  const [result, setResult] = useState<ResyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resync = useCallback(async (conversationId: string): Promise<ResyncResult> => {
    if (!conversationId) {
      const errResult = { success: false, error: "No conversation ID provided" };
      setError(errResult.error);
      return errResult;
    }

    setIsResyncing(true);
    setError(null);
    setResult(null);

    try {
      // Dedicated resync webhook — fetches ALL messages from GHL and upserts to Supabase
      const data = (await securelyTriggerN8n(N8N_WEBHOOKS.ALTARA_GHL_CONVERSATION_RESYNC, {
        conversation_id: conversationId,
      })) as ResyncResult;

      setResult(data);

      if (!data.success) {
        setError(data.error || "Resync failed");
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error during resync";
      const errResult: ResyncResult = { success: false, error: errorMessage };
      setError(errorMessage);
      setResult(errResult);
      return errResult;
    } finally {
      setIsResyncing(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    resync,
    isResyncing,
    result,
    error,
    reset,
  };
}
